import type { JSX } from 'react';

import { Avatar } from '@change/ds-core-ui/next';

import { Translate, TranslateHtml } from '@change-corgi/core/react/i18n';

import { InfoTooltip } from 'src/app/shared/components/tooltips';
import { useProcessedImageUrl } from 'src/app/shared/hooks/image';

import { ExportButton } from '../../../../../shared/ExportButton';
import { useExportButton } from '../../../../../shared/ExportButton/hooks/useExportButton';
import { ExportNotifications } from '../../../../../StarterDashboardComments/components/ExportNotifications';

import { type RecentSupporter, useRecentSupportersList } from './hooks/useRecentSupportersList';

// Component to handle individual supporter avatar processing
function SupporterItem({ supporter }: { supporter: RecentSupporter }) {
	const processedPhoto = useProcessedImageUrl(supporter.photo);

	return (
		<li
			className="flex w-full items-center gap-2"
			key={supporter.uniqueKey}
			data-testid={`recent-supporter-item-new-${supporter.uniqueKey}`}
		>
			{/* Avatar */}
			<Avatar
				alt="avatar"
				size="small"
				src={processedPhoto}
				data-testid={`recent-supporter-avatar-new-${supporter.uniqueKey}`}
			/>

			{/* Name and timestamp */}
			<p
				className="text-content-strong-color font-sans text-size-body"
				data-testid={`recent-supporter-text-new-${supporter.uniqueKey}`}
			>
				<TranslateHtml
					value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.name_with_sign_time"
					replacements={{
						name: supporter.displayName,
						relative_time: supporter.relativeTimestamp,
					}}
				/>
			</p>
		</li>
	);
}

export function RecentSupportersList(): JSX.Element {
	const { data, actions } = useRecentSupportersList();
	const { data: exportData, actions: exportActions } = useExportButton('signatures');

	return (
		<div
			className="relative flex h-[441px] w-full flex-col overflow-hidden bg-white"
			data-qa="starter-dashboard-recent-supporters-card-new"
			data-testid="starter-dashboard-recent-supporters-card-new"
			aria-label="export-recent-supporters-card-new"
			aria-describedby="export-recent-supporters-card-new"
		>
			{/* Header Section */}
			<div className="flex items-center gap-2 pb-[1px]" data-testid="recent-supporters-header-new">
				<h3
					className="text-content-strong-color font-sans text-size-heading-xs font-bold md:text-size-heading-small"
					id="recent-supporters-title-new"
					data-testid="recent-supporters-title-new"
				>
					<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.title" />
				</h3>
				<InfoTooltip aria-describedby="recent-supporters-title-new" data-testid="recent-supporters-tooltip-new">
					<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.tip" />
				</InfoTooltip>
				<div className="ml-auto justify-self-end" data-testid="recent-supporters-export-container-new">
					<ExportButton
						exportType="SIGNATURES"
						data-testid="export-recent-supporters-button-new"
						data-qa="export-recent-supporters-button-new"
						aria-label="export-recent-supporters-button-new"
						aria-describedby="export-recent-supporters-button-new"
						data={exportData}
						actions={exportActions}
					/>
				</div>
			</div>

			{/* Supporters List */}
			<div className="overflow-hidden backdrop-blur-sm">
				<ul
					className="flex h-[357px] max-h-[357px] flex-1 flex-col gap-4 overflow-auto p-0 pt-5 md:p-0 md:pt-6"
					sx={{
						maskImage: 'linear-gradient(to top, transparent 0%, black 30%)',
						WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 30%)',
					}}
					onScroll={actions.onScrollSupporters}
					data-testid="recent-supporters-list-new"
				>
					{data.recentSupporters.map((supporter) => (
						<SupporterItem key={supporter.uniqueKey} supporter={supporter} />
					))}
					<li className="min-h-12" />
				</ul>
			</div>

			<ExportNotifications data={exportData} />
		</div>
	);
}
